import { useEffect, useMemo, useState } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import experianLogo from '../../../../assets/images/experian.png';
import { COLORS } from '../../../../utils/colors';

interface RiskBand {
  label: string;
  min: number;
  max: number;
  color: string;
  bg: string;
  text: string;
  dot: string;
}

interface GaugeSVGProps {
  score: number;
  band: RiskBand;
  min: number;
  max: number;
}

interface ScoreTrendPoint {
  quarter: string;
  score: number;
  industryScore?: number | null;
}

interface ExperianScoreCardProps {
  intelliscore?: number;
  financialScoreRisk?: number;
  creditLimit?: string;
  fsrScoreTrends?: ScoreTrendPoint[];
  commercialScoreTrends?: ScoreTrendPoint[];
  // Determines which score scale the supplier's report actually uses (see
  // band sets below) -- Experian's Intelliscore Plus V2 model (000254, Dill's
  // default) reports 0-100 percentiles; other/legacy models report 300-850.
  modelCode?: string;
}

// Dill's default Experian model (see DEFAULT_EXPERIAN_MODEL_CODE in
// experianCreditReportService.ts) -- Intelliscore Plus V2, scored 1-100 percentile.
const INTELLISCORE_PLUS_V2_MODEL_CODE = '000224';

// Legacy/other Experian models report on a 300-850 scale.
const LEGACY_BANDS_300_850: RiskBand[] = [
  {
    label: 'High Risk',
    min: 300,
    max: 600,
    color: '#C0392B',
    bg: '#FCEBEB',
    text: '#A32D2D',
    dot: '#C0392B',
  },
  {
    label: 'Medium High Risk',
    min: 601,
    max: 660,
    color: '#FA8C27',
    bg: '#FFF0E0',
    text: '#A05010',
    dot: '#FA8C27',
  },
  {
    label: 'Medium Risk',
    min: 661,
    max: 720,
    color: '#EF9F27',
    bg: '#FAEEDA',
    text: '#854F0B',
    dot: '#EF9F27',
  },
  {
    label: 'Medium Low Risk',
    min: 721,
    max: 780,
    color: '#5DCAA5',
    bg: '#E1F5EE',
    text: '#0F6E56',
    dot: '#5DCAA5',
  },
  {
    label: 'Low Risk',
    min: 781,
    max: 850,
    color: '#1D9E75',
    bg: '#EAF3DE',
    text: '#3B6D11',
    dot: '#1D9E75',
  },
];

// Intelliscore Plus V2's real published risk-class cutoffs (0-100 percentile),
// taken from Experian's own rendered gauge: risk class 5 (High Risk) 0-10,
// down to risk class 1 (Low Risk) 75-100.
const INTELLISCORE_PLUS_BANDS_0_100: RiskBand[] = [
  {
    label: 'High Risk',
    min: 0,
    max: 10,
    color: '#C0392B',
    bg: '#FCEBEB',
    text: '#A32D2D',
    dot: '#C0392B',
  },
  {
    label: 'Medium High Risk',
    min: 10,
    max: 25,
    color: '#FA8C27',
    bg: '#FFF0E0',
    text: '#A05010',
    dot: '#FA8C27',
  },
  {
    label: 'Medium Risk',
    min: 25,
    max: 50,
    color: '#EF9F27',
    bg: '#FAEEDA',
    text: '#854F0B',
    dot: '#EF9F27',
  },
  {
    label: 'Medium Low Risk',
    min: 50,
    max: 75,
    color: '#5DCAA5',
    bg: '#E1F5EE',
    text: '#0F6E56',
    dot: '#5DCAA5',
  },
  {
    label: 'Low Risk',
    min: 75,
    max: 100,
    color: '#1D9E75',
    bg: '#EAF3DE',
    text: '#3B6D11',
    dot: '#1D9E75',
  },
];

// Financial Stability Risk's real published risk-class cutoffs (0-100
// percentile), also taken from Experian's own rendered gauge -- FSR's bands
// sit at different cutoffs than Intelliscore Plus's.
const FSR_BANDS_0_100: RiskBand[] = [
  {
    label: 'High Risk',
    min: 0,
    max: 3,
    color: '#C0392B',
    bg: '#FCEBEB',
    text: '#A32D2D',
    dot: '#C0392B',
  },
  {
    label: 'Medium High Risk',
    min: 3,
    max: 10,
    color: '#FA8C27',
    bg: '#FFF0E0',
    text: '#A05010',
    dot: '#FA8C27',
  },
  {
    label: 'Medium Risk',
    min: 10,
    max: 30,
    color: '#EF9F27',
    bg: '#FAEEDA',
    text: '#854F0B',
    dot: '#EF9F27',
  },
  {
    label: 'Medium Low Risk',
    min: 30,
    max: 65,
    color: '#5DCAA5',
    bg: '#E1F5EE',
    text: '#0F6E56',
    dot: '#5DCAA5',
  },
  {
    label: 'Low Risk',
    min: 65,
    max: 100,
    color: '#1D9E75',
    bg: '#EAF3DE',
    text: '#3B6D11',
    dot: '#1D9E75',
  },
];

function isPercentileModel(modelCode?: string): boolean {
  return modelCode === INTELLISCORE_PLUS_V2_MODEL_CODE;
}

function getBand(score: number, bands: RiskBand[]): RiskBand {
  return bands.find((b) => score >= b.min && score <= b.max) ?? bands[0];
}

function scoreToPercent(score: number, min: number, max: number): number {
  const clamped = Math.min(max, Math.max(min, score));
  return ((clamped - min) / (max - min)) * 100;
}

function GaugeSVG({ score, band, min, max }: GaugeSVGProps) {
  const [animated, setAnimated] = useState<number>(0);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(scoreToPercent(score, min, max)), 100);
    return () => clearTimeout(t);
  }, [score, min, max]);

  const R = 80;
  const cx = 110;
  const cy = 110;
  const startAngle = Math.PI;
  const totalArc = Math.PI;

  const arcPath = (start: number, end: number, r: number): string => {
    const s = { x: cx + r * Math.cos(start), y: cy + r * Math.sin(start) };
    const e = { x: cx + r * Math.cos(end), y: cy + r * Math.sin(end) };
    const large = end - start > Math.PI ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  const angle = startAngle + totalArc * (animated / 100);
  const knobX = cx + R * Math.cos(angle);
  const knobY = cy + R * Math.sin(angle);

  return (
    <svg viewBox="0 0 220 130" width="220" height="130">
      <path
        d={arcPath(Math.PI, 2 * Math.PI, R)}
        fill="none"
        stroke="#E8E6DF"
        strokeWidth="14"
        strokeLinecap="round"
      />
      <path
        d={arcPath(Math.PI, Math.PI + totalArc * (animated / 100), R)}
        fill="none"
        stroke={band.color}
        strokeWidth="14"
        strokeLinecap="round"
        style={{ transition: 'all 1.2s cubic-bezier(0.4,0,0.2,1)' }}
      />
      <circle
        cx={knobX}
        cy={knobY}
        r="9"
        fill="white"
        stroke={band.color}
        strokeWidth="3"
        style={{ transition: 'all 1.2s cubic-bezier(0.4,0,0.2,1)' }}
      />
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="34" fontWeight="600" fill={band.color}>
        {score}
      </text>
      <text x={cx} y={cy + 18} textAnchor="middle" fontSize="11" fill="#888780">
        range: {min} – {max}
      </text>
    </svg>
  );
}

export default function ExperianScoreCard({
  intelliscore = 600,
  financialScoreRisk,
  creditLimit,
  fsrScoreTrends,
  commercialScoreTrends,
  modelCode,
}: ExperianScoreCardProps) {
  const percentileModel = isPercentileModel(modelCode);
  const intelliscoreBands = percentileModel ? INTELLISCORE_PLUS_BANDS_0_100 : LEGACY_BANDS_300_850;
  const fsrBands = percentileModel ? FSR_BANDS_0_100 : LEGACY_BANDS_300_850;
  const gaugeMin = intelliscoreBands[0].min;
  const gaugeMax = intelliscoreBands[intelliscoreBands.length - 1].max;

  const band = getBand(intelliscore, intelliscoreBands);
  const fsrBand = financialScoreRisk != null ? getBand(financialScoreRisk, fsrBands) : undefined;

  const trendDataset = useMemo(() => {
    const fsr = [...(fsrScoreTrends ?? [])].reverse();
    const commercial = [...(commercialScoreTrends ?? [])].reverse();
    const length = Math.max(fsr.length, commercial.length);
    return Array.from({ length }, (_, i) => ({
      quarter: fsr[i]?.quarter ?? commercial[i]?.quarter ?? '',
      fsr: fsr[i]?.score ?? null,
      commercial: commercial[i]?.score ?? null,
    }));
  }, [fsrScoreTrends, commercialScoreTrends]);

  return (
    <div className="flex flex-col items-center justify-center bg-white rounded-lg border border-[#E8E6DF] w-full p-2 mb-4">
      <div className="w-full grid grid-cols-[1fr_1.4fr_1fr] items-center p-2.5 border-b border-[#EEECE6]">
        <div className="flex flex-col gap-1.5">
          <div className="w-full mb-2">
            <img src={experianLogo} alt="Experian" className="w-auto h-8 object-contain" />
          </div>
          <span className="text-[11px] text-[#888780] uppercase tracking-[0.08em]">Risk Level</span>
          <span
            className="inline-flex items-center gap-[7px] px-4 py-[7px] rounded-[30px] text-[13px] font-semibold w-fit border"
            style={{
              background: band.bg,
              color: band.text,
              borderColor: `${band.color}44`,
            }}>
            <span
              className="inline-block w-[9px] h-[9px] rounded-full shrink-0"
              style={{ background: band.color }}
            />
            {band.label}
          </span>
        </div>

        <div className="flex flex-col items-center">
          <span className="text-base text-[#888780] mb-0.5">Experian Score</span>
          <GaugeSVG score={intelliscore} band={band} min={gaugeMin} max={gaugeMax} />
        </div>

        <div className="text-right flex flex-col gap-2">
          <div>
            <span className="block text-xs text-[#888780] uppercase tracking-[0.08em] mb-1">
              Recommended Limit
            </span>
            <span className="text-xl font-bold text-[#1A1A18] tracking-tight">
              {creditLimit ?? '—'}
            </span>
          </div>
          {intelliscore != null && (
            <div>
              <span className="block text-[11px] text-[#888780] uppercase tracking-[0.08em]">
                Intelliscore Plus Score
              </span>
              <span className="text-sm font-semibold text-[#1A1A18]">{intelliscore}</span>
            </div>
          )}
          {financialScoreRisk != null && (
            <div>
              <span className="block text-[11px] text-[#888780] uppercase tracking-[0.08em]">
                Financial Stability Risk Score
              </span>
              <span
                className="text-sm font-semibold"
                style={{ color: fsrBand?.color ?? '#1A1A18' }}>
                {financialScoreRisk}
              </span>
            </div>
          )}
        </div>
      </div>

      {trendDataset.length > 0 && (
        <div className="p-2.5 w-full border-b border-[#EEECE6]">
          <p className="text-xs text-[#888780] uppercase tracking-[0.08em] mb-3.5">Score Trends</p>
          <LineChart
            dataset={trendDataset}
            height={220}
            margin={{ left: 0, right: 16, top: 16, bottom: 32 }}
            xAxis={[
              {
                dataKey: 'quarter',
                scaleType: 'point',
                tickLabelStyle: {
                  fontSize: 11,
                  fill: COLORS.TEXT_SECONDARY,
                  fontFamily: 'Inter',
                },
                disableLine: true,
                disableTicks: true,
              },
            ]}
            series={[
              {
                dataKey: 'fsr',
                label: 'FSR Score',
                color: band.color,
                curve: 'monotoneX',
                showMark: true,
                connectNulls: true,
              },
              {
                dataKey: 'commercial',
                label: 'Commercial Score',
                color: COLORS.PRIMARY_500,
                curve: 'monotoneX',
                showMark: true,
                connectNulls: true,
              },
            ]}
            yAxis={[
              {
                width: 36,
                tickLabelStyle: {
                  fontSize: 10,
                  fill: COLORS.TEXT_SECONDARY,
                  fontFamily: 'Inter',
                },
                disableLine: true,
                disableTicks: true,
              },
            ]}
            grid={{ horizontal: true, vertical: false }}
            sx={{
              '& .MuiChartsGrid-line': {
                stroke: COLORS.GREY_100,
                strokeDasharray: '3 3',
              },
            }}
          />
        </div>
      )}

      <div className="p-2.5 w-full">
        <p className="text-xs text-[#888780] uppercase tracking-[0.08em] mb-3.5">
          Risk Scale Reference
        </p>
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>
              {['Risk Level', 'Score Range'].map((h) => (
                <th
                  key={h}
                  className="text-left text-[#888780] font-medium px-3 py-1.5 border-b border-[#EEECE6] text-xs">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {intelliscoreBands.map((b) => {
              const isActive = b.label === band.label;
              return (
                <tr
                  key={b.label}
                  className="transition-colors duration-200"
                  style={{ background: isActive ? b.bg : 'transparent' }}>
                  <td className="px-3 py-[9px] border-b border-[#EEECE6]">
                    <span
                      className="inline-flex items-center gap-2 px-[11px] py-[3px] rounded-[20px] text-xs font-medium"
                      style={{ background: b.bg, color: b.text }}>
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: b.dot }}
                      />
                      {b.label}
                      {isActive && <span className="ml-[5px] text-[10px]">◀ current</span>}
                    </span>
                  </td>
                  <td
                    className={`px-3 py-[9px] border-b border-[#EEECE6] tabular-nums text-xs ${
                      isActive ? 'font-bold' : 'font-normal'
                    }`}
                    style={{ color: isActive ? b.color : '#444441' }}>
                    {b.min} – {b.max}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
